import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ChatboxColors, Crisp } from "crisp-sdk-web";
import { PostHogProvider } from "posthog-js/react";
import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";

import { MedibaseComponentsProvider } from "@meditect/medibase-components";

import { ClaimsProvider } from "@/features/auth/components/ClaimsProvider";
import { localStoragePersister, queryClient } from "@/queryClient";
import "@meditect/medibase-components/index.css";

import App from "./App";
import { UserProvider } from "./features/auth/components/UserProvider";
import "./features/sentry/instrument";

import { SentryProvider } from "./features/sentry/SentryProvider";
import "./index.css";

/** @lexical/react/LexicalMarkdownShortcutPlugin need prismjs */
import "prismjs";
import "prismjs/themes/prism.css";

import { env } from "./env";

const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

const normalizePathname = (pathname: string) => {
  return pathname
    .split("/")
    .map((segment) => (UUID_REGEX.test(segment) ? ":uuid" : segment))
    .join("/");
};

const UNTRACKED_ROUTES = ["/ventes/ajouter", "/ventes"];

if (env.REACT_APP_CRISP_WEBSITE_ID) {
  Crisp.configure(env.REACT_APP_CRISP_WEBSITE_ID, {
    autoload: false,
    locale: "fr",
  });
  Crisp.setColorTheme(ChatboxColors.Green);
}

const container = document.querySelector("#root");
const root = createRoot(container!);

const PHProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  return env.REACT_APP_PUBLIC_POSTHOG_KEY ? (
    <PostHogProvider
      apiKey={env.REACT_APP_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: env.REACT_APP_PUBLIC_POSTHOG_HOST,
        autocapture: false,
        capture_pageview: "history_change",
        capture_pageleave: false,
        capture_exceptions: false,
        rageclick: false,
        disable_session_recording: true,
        person_profiles: "identified_only",
        persistence: "localStorage",
        loaded: (ph) => {
          if (
            location.hostname === "localhost" ||
            location.hostname === "127.0.0.1" ||
            location.hostname === "pharmacie-preprod.meditect.com"
          ) {
            ph.opt_out_capturing(); // opts a user out of event capture
            ph.set_config({ capture_pageview: false });
          }
        },
        before_send: (event) => {
          if (event?.event === "$autocapture") {
            return null;
          }

          if (event?.event === "$pageview") {
            const url = event.properties?.$current_url;
            if (!url) {
              return null;
            }

            const parsed = new URL(url);
            const normalizedPath = normalizePathname(parsed.pathname);

            event.properties.$pathname = normalizedPath;
            event.properties.page_type = normalizedPath;

            // ❌ routes non métier
            if (UNTRACKED_ROUTES.includes(normalizedPath)) {
              return null;
            }

            // ✅ sampling pages détail (80% OFF)
            if (normalizedPath === "/ventes/:uuid") {
              return Math.random() < 0.2 ? event : null;
            }
          }

          return event;
        },
      }}
    >
      {children}
    </PostHogProvider>
  ) : (
    children
  );
};

root.render(
  // <React.StrictMode>
  <SentryProvider>
    <PHProvider>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: localStoragePersister }}>
        <ClaimsProvider>
          <MedibaseComponentsProvider>
            <HelmetProvider>
              <UserProvider>
                <App />
              </UserProvider>
            </HelmetProvider>
          </MedibaseComponentsProvider>
        </ClaimsProvider>
        <ReactQueryDevtools />
      </PersistQueryClientProvider>
    </PHProvider>
  </SentryProvider>,
  // </React.StrictMode>,
);
